import { action, observable } from 'mobx'
import { message } from 'antd'
import * as _ from 'lodash'
import { StoreExt } from '@utils/reactExt'
import {
  ICheckNick,
  IDeleteMessages,
  IMessageList,
  IMessageOne, IMessageOneForUser,
  IMessageUpdate,
  IReadMessages,
  IReceiveItem,
  ISendMessage,
  Schema$Message,
} from '@v2/protocol/message'
import { authStore, clubStore, matchupStore, userStore } from '@store/index'
import { ERR } from 'adv-protocol/src/ErrorCodes'
import { getErrorText } from '@game/errtext'
import { DUPLICATE_REQUEST_EXCEPTION, MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY } from '@constants/index'
import { isMobile } from 'react-device-detect'

export type MessageList = IMessageList.Schema

export interface MessageRecordSource {
  index?: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY
  tracks?: Schema$Message[]
  lastFetched?: Schema$Message[]
  hasMore?: boolean
  limit?: number
  total?: number
  readableCount?: number
  receivedItemCount?: number
}

export interface ReplyTargetUser {
  id: string
  nick: string
  title: string
}

export class MessageStore extends StoreExt {
  fetchIndex: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY

  messageList: MessageRecordSource

  receiveUser: ICheckNick.Schema

  @observable
  readableCount: number

  @observable
  receivedItemCount: number

  @observable
  totalReceiveMessageCount: number

  @observable
  totalSendMessageCount: number

  @observable
  unReadClubMessage: IMessageOne.Schema

  @observable
  unQuickReadUserMessage: IMessageOneForUser.Schema

  unReadClubMessagePopUp = false

  setTotalCount = (count: number) => {
    this.messageList.total = count
  }

  @action
  sendMessage = async (params: ISendMessage.Params): Promise<string> => {
    try {
      const { errcode } = await this.api.message.send(params)
      if (errcode) {
        if (errcode === ERR.PROCESSING_LIVE_BET_EXIST) {
          throw getErrorText(errcode, '라이브 베팅이 진행중인 경우 선물할 수 없습니다.')
        } else {
          throw getErrorText(errcode) || '서버 연결이 원활하지 않습니다. 새로고침해 주세요.'
        }
      }

      const res = await this.api.user.getUserInfo({})
      if (!res) {
        throw getErrorText(ERR.USER_NOT_FOUND)
      }

      const { error } = res
      if (error === DUPLICATE_REQUEST_EXCEPTION) {
        return ''
      }

      const { user } = res
      authStore.setUser(user)
      return ''
    } catch (err) {
      console.error(err)
      return err
    }
  }

  @action
  readMessage = async (params: IReadMessages.Params) => {
    try {
      const result = await this.api.message.read(params)
      userStore.decreaseUnReadMessageCount(result?.unReadMessageDecreaseCount)
      userStore.decreaseUnQuickReadMessageCount(result?.unQuickReadMessageDecreaseCount)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  quickReadMessage = async (params: IReadMessages.Params) => {
    try {
      await this.api.message.quickRead(params)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  deleteMessage = async (params: IDeleteMessages.Params) => {
    try {
      const result = await this.api.message.delete(params)
      userStore.decreaseUnReadMessageCount(result?.unReadMessageDecreaseCount)
      userStore.decreaseUnQuickReadMessageCount(result?.unQuickReadMessageDecreaseCount)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  receiveMessage = async (params: IReceiveItem.Params): Promise<IReceiveItem.Schema> => {
    try {
      const result = await this.api.message.receive(params)
      userStore.decreaseUnReadMessageCount(result?.unReadMessageDecreaseCount)
      userStore.decreaseUnQuickReadMessageCount(result?.unQuickReadMessageDecreaseCount)
      return result
    } catch (err) {
      console.error(err)
      return err
    }
  }

  @action
  checkNick = async (params: ICheckNick.Params) => {
    try {
      const res = await this.api.message.checkNick(params)
      this.setReceiveUser(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setReceiveUser = (receiveUser: ICheckNick.Schema): ICheckNick.Schema => {
    this.receiveUser = receiveUser
    return receiveUser
  }

  @action
  setReadableCount = (count: number) => {
    this.readableCount = count
  }

  @action
  setReceivedItemCount = (count: number) => {
    this.receivedItemCount = count
  }

  @action
  setTotalReceiveMessageCount = (count: number) => {
    this.totalReceiveMessageCount = count
  }

  @action
  setTotalSendMessageCount = (count: number) => {
    this.totalSendMessageCount = count
  }

  @action
  fetchMessageList = async (
    index: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY,
    amount: number,
    dir: number,
    clubId: string,
    isClubMessage: boolean,
    resetForce = false,
  ): Promise<MessageRecordSource> => {
    try {
      const reset = resetForce === true || this.fetchIndex !== index
      const targetLength = this.messageList ? this.messageList.tracks.length : 0
      const offset = reset ? 0 : targetLength
      const {
        limit,
        total,
        messages,
        readableCount,
        receivedItemCount,
        totalReceiveMessageCount,
        totalSendMessageCount
      } = await this.api.message.getMessageList({
        dir,
        offset,
        clubId,
        isClubMessage,
        limit: amount,
      })
      this.fetchIndex = index
      this.setReadableCount(readableCount)
      this.setReceivedItemCount(receivedItemCount)
      this.setTotalReceiveMessageCount(totalReceiveMessageCount)
      this.setTotalSendMessageCount(totalSendMessageCount)

      if (reset) {
        return this.resetTracks(index, { limit, total, messages, readableCount, receivedItemCount })
      }
      return this.updateTracks(index, { limit, total, messages, readableCount, receivedItemCount })
    } catch (err) {
      console.error(err)

      message.error('메시지 페이지를 정상적으로 불러오지 못하였습니다.')
    }

    return null
  }

  @action
  resetTracks = (index: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY, fetchData: MessageList): MessageRecordSource => {
    this.messageList = {}
    return this.updateTracks(index, fetchData)
  }

  @action
  updateTracks = (index: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY, fetchData: MessageList): MessageRecordSource => {
    const { limit, total, messages, readableCount, receivedItemCount } = fetchData
    const { tracks = [] } = this.messageList
    const newTracks: Schema$Message[] = _.cloneDeep(tracks)
    const lastFetched: Schema$Message[] = []

    for (const el of messages) {
      newTracks.push(el)
      lastFetched.push(el)
    }

    const hasMore = newTracks.length < total
    const newSource = { index, tracks: newTracks, lastFetched, hasMore, limit, total, readableCount, receivedItemCount }

    this.messageList = newSource

    return newSource
  }

  @action
  getOneUnReadMessageByClubId = async (params: IMessageOne.Params) => {
    try {
      const res = await this.api.message.getOneUnReadClubMessageByClubId(params)
      const { error } = res
      if (!res || error === DUPLICATE_REQUEST_EXCEPTION) {
        return
      }

      if (res && res.data) {
        this.setUnReadClubMessage(res.data)
        if (authStore.isSignedIn) {
          clubStore.fetchUnReadClubMessageCount(params.clubId)
        }
        if (res.data.receiverread === 0) {
          this.unReadClubMessagePopUp = true
        } else {
          this.unReadClubMessagePopUp = false
        }
      } else {
        this.unReadClubMessagePopUp = false
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  doConfirmReadClubMessageByClubIdAndId = async (params: IMessageUpdate.Params) => {
    try {
      await this.api.message.doConfirmReadClubMessageByClubIdAndId(params)
      this.unReadClubMessagePopUp = false
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setUnReadClubMessage = (data: IMessageOne.Schema | null): IMessageOne.Schema => {
    this.unReadClubMessage = data
    return data
  }

  @action
  setupUnReadClubMessage(data: IMessageOne.Schema, clubId: string) {
    if (data) {
      this.unReadClubMessagePopUp = data.receiverread === 0
      this.unReadClubMessage = data
      if (authStore.isSignedIn) {
        clubStore.fetchUnReadClubMessageCount(clubId)
      }
    } else {
      this.unReadClubMessagePopUp = false
      matchupStore.setUnreadClubMessageCount(0)
    }
    return data
  }

  @action
  setUnQuickReadUserMessage(data: IMessageOneForUser.Schema): void {
    this.unQuickReadUserMessage = data
  }

  getOneUnQuickReadMessageByUserId = async () => {
    try {
      const res = await this.api.message.getOneUnQuickReadMessageByUserId()
      this.setUnQuickReadUserMessage(res.data)
    } catch (err) {
      this.setUnQuickReadUserMessage(null)
      console.error(err)
    }
  }

  documentVisibleChangeHandler(isHidden: boolean): void {
    if (!isMobile) {
      return
    }

    if (!authStore.isSignedIn) {
      return
    }

    if (isHidden) {
      return
    }

    const { club } = matchupStore
    if (_.isEmpty(_.trim(club?.id))) {
      return
    }
    this.getOneUnReadMessageByClubId({ clubId: club.id })
  }
}

export default new MessageStore()
